import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const STORAGE_KEY = "fs_install_dismissed_at";
const SUPPRESS_DAYS = 14;

// Module-level state so other components (e.g. AccountSection's "Install" button) can
// trigger the prompt without re-implementing the OAuth dance with the browser.
let _deferredPrompt = null;
const listeners = new Set();
function setDeferred(p) {
  _deferredPrompt = p;
  listeners.forEach((fn) => fn(p));
}
export function getDeferredInstallPrompt() {
  return _deferredPrompt;
}
export function subscribeInstallPrompt(fn) {
  listeners.add(fn);
  fn(_deferredPrompt);
  return () => listeners.delete(fn);
}
/** Try to trigger the native install prompt. Falls back to a manual hint flag. */
export async function triggerInstall() {
  const p = _deferredPrompt;
  if (!p) return { triggered: false, outcome: "no_prompt" };
  try {
    p.prompt();
    const choice = await p.userChoice;
    if (choice?.outcome === "accepted") {
      setDeferred(null);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
    return { triggered: true, outcome: choice?.outcome || "unknown" };
  } catch {
    return { triggered: false, outcome: "error" };
  }
}

/** Detect whether the app is already installed. */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

/**
 * PWAInstallPrompt
 * - Listens for `beforeinstallprompt` (Chrome/Edge/Android Chrome)
 * - Shows a floating "Install FlashSpecs" pill the user can tap
 * - On iOS Safari (no native event), shows an "Add to Home Screen" hint
 * - On Android browsers that don't fire the native event (Brave, Firefox, Samsung
 *   Internet), shows a generic "tap browser menu" hint after a delay
 * - Suppressed for 14 days after dismissal or successful install
 */
export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (isStandalone()) return;

    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < SUPPRESS_DAYS * 24 * 60 * 60 * 1000) return;

    const iosLike = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    const androidLike = /Android/i.test(ua);
    setIsIOS(iosLike);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      setDeferredPrompt(null);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    let fallbackTimer;
    if (iosLike) {
      fallbackTimer = setTimeout(() => setVisible(true), 4500);
    } else if (androidLike) {
      fallbackTimer = setTimeout(() => {
        setDeferredPrompt((cur) => {
          if (!cur) setVisible(true);
          return cur;
        });
      }, 6000);
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    const result = await triggerInstall();
    if (result.outcome === "accepted") setVisible(false);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 px-4 py-3 rounded-full shadow-2xl border border-[#C36A2F]/40 bg-[#0a0a0a] max-w-[90vw]"
      data-testid="pwa-install-prompt"
    >
      <div className="w-9 h-9 rounded-full bg-[#C36A2F]/20 flex items-center justify-center shrink-0">
        <Download size={16} className="text-[#C36A2F]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[#F5F0E6] text-sm font-semibold">Install FlashSpecs</p>
        {isIOS ? (
          <p className="text-[11px] text-[#F5F0E6]/65 leading-snug mt-0.5">
            Tap <span className="font-bold">Share</span> → <span className="font-bold">Add to Home Screen</span>
          </p>
        ) : deferredPrompt ? (
          <p className="text-[11px] text-[#F5F0E6]/65 leading-snug mt-0.5">
            Add to your home screen for faster access.
          </p>
        ) : (
          <p className="text-[11px] text-[#F5F0E6]/65 leading-snug mt-0.5">
            Tap browser menu (<span className="font-bold">⋮</span>) → <span className="font-bold">Install app</span> / <span className="font-bold">Add to Home Screen</span>
          </p>
        )}
      </div>
      {deferredPrompt && !isIOS && (
        <button
          onClick={handleInstall}
          className="px-3 py-1.5 rounded-full bg-[#C36A2F] text-black text-xs font-bold hover:bg-[#d97a35] transition-colors shrink-0"
          data-testid="pwa-install-btn"
        >
          Install
        </button>
      )}
      <button
        onClick={handleDismiss}
        className="text-[#F5F0E6]/40 hover:text-[#F5F0E6] transition-colors shrink-0"
        aria-label="Dismiss"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
